/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  GetOption,
  HateoasResourceOperation,
  HttpMethod,
  PagedGetOption,
  PagedResourceCollection,
  Resource,
  ResourceCollection,
} from '@lagoshny/ngx-hateoas-client';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, filter, map, of, switchMap, take, tap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { RgsHttpError, RgsHttpErrorResponse } from '../models/http-error.model';
import { RxJSUtils } from '../utils/rxjs.utils';
import { SuffixUtils } from '../utils/suffix.utils';

export enum ActionType {
  LOAD_SELECTED = 'loadSelected',
  LOAD_ALL = 'loadAll',
  LOAD_MORE = 'loadMore',
  CREATE = 'create',
  DELETE = 'delete',
  UPDATE = 'update',
  UPDATE_MINOR = 'updateMinor',
  DUPLICATE = 'duplicate',
  IMPORT = 'import',
  EXPORT = 'export',
}

export interface Action {
  id: string;
  type: ActionType;
  status: 'pending' | 'success' | 'error';
  data?: {
    id?: string;
    name?: string;
  };
  userName?: string;
  userId?: string;
  message?: string;
  httpError?: RgsHttpError | null;
}

export function actionToLoading(action$: Observable<Action>, actionType: ActionType): Observable<boolean> {
  return action$.pipe(
    filter((action) => action.type === actionType),
    map((action) => action.status === 'pending'),
  );
}

export function hasPendingActions(actions$: Observable<Action>): Observable<boolean> {
  const pendingActions = new Map<string, Set<string>>();

  return actions$.pipe(
    tap((action) => {
      if (!pendingActions.has(action.type)) {
        pendingActions.set(action.type, new Set());
      }

      const actionSet = pendingActions.get(action.type);

      if (actionSet) {
        if (action.status === 'pending') {
          actionSet.add(action.id);
        } else if (action.status === 'success' || action.status === 'error') {
          actionSet.delete(action.id);
        }

        if (actionSet.size === 0) {
          pendingActions.delete(action.type);
        }
      }
    }),
    map(() => Array.from(pendingActions.values()).some((set) => set.size > 0)),
  );
}

export class CommonState<T extends Resource> extends HateoasResourceOperation<T> {
  private readonly commonStateTranslateService = inject(TranslateService);

  protected _selected$ = new BehaviorSubject<T | undefined>(undefined);
  public selected$ = this._selected$.asObservable() as Observable<T | undefined>;
  public selectedSignal = toSignal(this.selected$);
  protected selectedOperators: any[] = [];
  protected selectedMutationCallback: (item: T) => T = (item: T) => item;

  protected _loadingSelected$ = new BehaviorSubject<boolean>(false);
  public loadingSelected$ = this._loadingSelected$.asObservable();

  protected _items$ = new BehaviorSubject<T[]>([]);
  public items$ = this._items$.asObservable();
  protected itemsOperators: any[] = [];

  protected _count$ = new BehaviorSubject<number>(0);
  public count$ = this._count$.asObservable();

  protected _loadingCount$ = new BehaviorSubject<boolean>(false);
  public loadingCount$ = this._loadingCount$.asObservable();

  protected _loadingItems$ = new BehaviorSubject<boolean>(false);
  public loadingItems$ = this._loadingItems$.asObservable();

  protected _loadingAction$ = new BehaviorSubject<boolean>(false);
  public loadingAction$ = this._loadingAction$.asObservable();

  protected _action$ = new BehaviorSubject<Action | undefined>(undefined);
  public action$: Observable<Action> = this._action$
    .asObservable()
    .pipe(filter((i) => i !== undefined)) as Observable<Action>;

  protected _loadingMinorAction$ = new BehaviorSubject<boolean>(false);
  public loadingMinorAction$ = this._loadingMinorAction$.asObservable();

  protected _minorAction$ = new BehaviorSubject<Action | undefined>(undefined);
  public minorAction$: Observable<Action> = this._minorAction$
    .asObservable()
    .pipe(filter((i) => i !== undefined)) as Observable<Action>;

  constructor(resourceType: any) {
    super(resourceType);
  }

  public loadSelected(id: number | string, options?: GetOption): void {
    const actionId = uuidv4();
    this._loadingSelected$.next(true);
    this._action$.next({
      id: actionId,
      type: ActionType.LOAD_SELECTED,
      status: 'pending',
    });

    this.getResource(id, options)
      .pipe(
        take(1),
        filter((i) => !!i),
        RxJSUtils.operatorFunctionsToOperatorFunction(this.selectedOperators),
        map((res) => res as T),
      )
      .subscribe({
        next: (res) => {
          this._loadingSelected$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.LOAD_SELECTED,
            status: 'success',
          });
          this._selected$.next(res);
        },
        error: (err: RgsHttpErrorResponse) => {
          this._loadingSelected$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.LOAD_SELECTED,
            status: 'error',
            httpError: err.error,
          });
        },
      });
  }

  public unloadSelected() {
    this._selected$.next(undefined);
  }

  public loadItems(query: string, options?: PagedGetOption): void {
    const actionId = uuidv4();
    this._loadingItems$.next(true);
    this._action$.next({
      id: actionId,
      type: ActionType.LOAD_ALL,
      status: 'pending',
    });

    this.searchCollection(query, options)
      .pipe(
        take(1),
        filter((i) => !!i),
        switchMap((resourceCollection) =>
          of(resourceCollection.resources).pipe(
            RxJSUtils.operatorFunctionsToOperatorFunction(this.itemsOperators),
            map(
              (resources: T[]) =>
                ({
                  ...resourceCollection,
                  resources,
                }) as ResourceCollection<T>,
            ),
          ),
        ),
      )
      .subscribe({
        next: (res) => {
          this._loadingItems$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.LOAD_ALL,
            status: 'success',
          });
          this._items$.next(res.resources);
        },
        error: (err: RgsHttpErrorResponse) => {
          this._loadingItems$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.LOAD_ALL,
            status: 'error',
            httpError: err.error,
          });
        },
      });
  }

  public loadPagedItems(query: string, options?: PagedGetOption): void {
    const actionId = uuidv4();
    this._loadingItems$.next(true);
    this._action$.next({
      id: actionId,
      type: ActionType.LOAD_ALL,
      status: 'pending',
    });

    this.searchPage(query, options)
      .pipe(
        take(1),
        filter((i) => !!i),
        switchMap((pagedResourceCollection) =>
          of(pagedResourceCollection.resources).pipe(
            RxJSUtils.operatorFunctionsToOperatorFunction(this.itemsOperators),
            map(
              (resources: T[]) =>
                ({
                  ...pagedResourceCollection,
                  resources,
                }) as PagedResourceCollection<T>,
            ),
          ),
        ),
      )
      .subscribe({
        next: (res) => {
          this._loadingItems$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.LOAD_ALL,
            status: 'success',
          });
          this._items$.next(res.resources);
          this._count$.next(res.totalElements);
        },
        error: (err: RgsHttpErrorResponse) => {
          this._loadingItems$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.LOAD_ALL,
            status: 'error',
            httpError: err.error,
          });
        },
      });
  }

  public customQueryLoadItems(
    method: HttpMethod,
    searchQuery: string,
    options?: PagedGetOption,
    requestBody?: any,
  ): void {
    const actionId = uuidv4();
    this._loadingItems$.next(true);
    this._action$.next({
      id: actionId,
      type: ActionType.LOAD_ALL,
      status: 'pending',
    });

    this.customSearchQuery<ResourceCollection<T>>(method, searchQuery, requestBody, options)
      .pipe(
        take(1),
        filter((i) => !!i),
        RxJSUtils.operatorFunctionsToOperatorFunction(this.itemsOperators),
      )
      .subscribe({
        next: (res: any) => {
          this._loadingItems$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.LOAD_ALL,
            status: 'success',
          });
          this._items$.next('resources' in res ? res.resources : res);
        },
        error: (err: RgsHttpErrorResponse) => {
          this._loadingItems$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.LOAD_ALL,
            status: 'error',
            httpError: err.error,
          });
        },
      });
  }

  public unloadItems() {
    this._items$.next([]);
  }

  public loadMoreItems(query: string, options?: PagedGetOption, idField: string = 'id'): void {
    const actionId = uuidv4();
    this._loadingItems$.next(true);
    this._action$.next({
      id: actionId,
      type: ActionType.LOAD_MORE,
      status: 'pending',
    });

    this.searchCollection(query, options)
      .pipe(
        take(1),
        filter((i) => !!i),
        switchMap((resourceCollection) =>
          of(resourceCollection.resources).pipe(
            RxJSUtils.operatorFunctionsToOperatorFunction(this.itemsOperators),
            map(
              (resources: T[]) =>
                ({
                  ...resourceCollection,
                  resources,
                }) as ResourceCollection<T>,
            ),
          ),
        ),
      )
      .subscribe({
        next: (res) => {
          this._loadingItems$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.LOAD_MORE,
            status: 'success',
          });
          const newItems = res.resources.filter((item: any) => {
            return !this.itemsValue.some((existingItem: any) => existingItem[idField] === item[idField]);
          });

          this._items$.next([...this.itemsValue, ...newItems]);
        },

        error: (err: RgsHttpErrorResponse) => {
          this._loadingItems$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.LOAD_MORE,
            status: 'error',
            httpError: err.error,
          });
        },
      });
  }

  public loadMoreItemsPaged(query: string, options?: PagedGetOption, idField: string = 'id'): void {
    const actionId = uuidv4();
    this._loadingItems$.next(true);
    this._action$.next({
      id: actionId,
      type: ActionType.LOAD_MORE,
      status: 'pending',
    });

    this.searchPage(query, options)
      .pipe(
        take(1),
        filter((i) => !!i),
        switchMap((pagedResourceCollection) =>
          of(pagedResourceCollection.resources).pipe(
            RxJSUtils.operatorFunctionsToOperatorFunction(this.itemsOperators),
            map(
              (resources: T[]) =>
                ({
                  ...pagedResourceCollection,
                  resources,
                }) as PagedResourceCollection<T>,
            ),
          ),
        ),
      )
      .subscribe({
        next: (res) => {
          this._loadingItems$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.LOAD_MORE,
            status: 'success',
          });
          const newItems = res.resources.filter((item: any) => {
            return !this.itemsValue.some((existingItem: any) => existingItem[idField] === item[idField]);
          });

          this._items$.next([...this.itemsValue, ...newItems]);
        },

        error: (err: RgsHttpErrorResponse) => {
          this._loadingItems$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.LOAD_MORE,
            status: 'error',
            httpError: err.error,
          });
        },
      });
  }

  public createItem(item: T, idField: string = 'id', nameField: string = 'naam') {
    const actionId = uuidv4();
    this._loadingAction$.next(true);
    this._action$.next({
      id: actionId,
      type: ActionType.CREATE,
      status: 'pending',
    });

    this.createResource({ body: this.selectedMutationCallback(item) })
      .pipe(
        take(1),
        RxJSUtils.operatorFunctionsToOperatorFunction(this.selectedOperators),
        map((res) => res as any),
      )
      .subscribe({
        next: (res) => {
          this._loadingAction$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.CREATE,
            status: 'success',
            data: {
              id: res[idField],
              name: res[nameField],
            },
          });
          this._items$.next([...this.itemsValue, res]);
          this._count$.next(this.countValue + 1);
        },
        error: (err: RgsHttpErrorResponse) => {
          this._loadingAction$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.CREATE,
            status: 'error',
            httpError: err.error,
          });
        },
      });
  }

  public updateItem(
    item: T,
    idField: string = 'id',
    nameField: string = 'naam',
    userActionType?: ActionType,
    userName?: (item: T) => string,
    userId?: (item: T) => string,
    updateList: boolean = true,
  ) {
    const actionId = uuidv4();
    this._loadingAction$.next(true);
    this._action$.next({
      id: actionId,
      type: ActionType.UPDATE,
      status: 'pending',
    });

    this.updateResource(this.selectedMutationCallback(item))
      .pipe(
        take(1),
        RxJSUtils.operatorFunctionsToOperatorFunction(this.selectedOperators),
        map((res) => res as any),
      )
      .subscribe({
        next: (res) => {
          this._loadingAction$.next(false);
          this._action$.next({
            id: actionId,
            type: userActionType || ActionType.UPDATE,
            status: 'success',
            data: {
              id: res[idField],
              name: res[nameField],
            },
            userName: userName ? userName(item) : res[nameField],
            userId: userId ? userId(item) : res[idField],
          });

          this._selected$.next(res);

          if (updateList) {
            const updatedItems = this.itemsValue.map((item: any) => {
              if (idField in item && item[idField] === res[idField]) {
                return res;
              } else {
                return item;
              }
            });

            this._items$.next(updatedItems);
          }
        },
        error: (err: RgsHttpErrorResponse) => {
          this._loadingAction$.next(false);
          this._action$.next({
            id: actionId,
            type: userActionType || ActionType.UPDATE,
            status: 'error',
            httpError: err.error,
          });
        },
      });
  }

  public updateItemById(
    item: T,
    idField: string = 'id',
    nameField: string = 'naam',
    userActionType?: ActionType,
    userName?: (item: T) => string,
    userId?: (item: T) => string,
  ) {
    const actionId = uuidv4();
    this._loadingAction$.next(true);
    this._action$.next({
      id: actionId,
      type: ActionType.UPDATE,
      status: 'pending',
    });

    this.updateResourceById((item as any)[idField], { body: this.selectedMutationCallback(item) })
      .pipe(
        take(1),
        RxJSUtils.operatorFunctionsToOperatorFunction(this.selectedOperators),
        map((res) => res as any),
      )
      .subscribe({
        next: (res) => {
          this._loadingAction$.next(false);
          this._action$.next({
            id: actionId,
            type: userActionType || ActionType.UPDATE,
            status: 'success',
            data: {
              id: res[idField],
              name: res[nameField],
            },
            userName: userName ? userName(item) : res[nameField],
            userId: userId ? userId(item) : res[idField],
          });

          const updatedItems = this.itemsValue.map((item: any) => {
            if (idField in item && item[idField] === res[idField]) {
              return res;
            } else {
              return item;
            }
          });
          this._selected$.next(res);
          this._items$.next(updatedItems);
        },
        error: (err: RgsHttpErrorResponse) => {
          this._loadingAction$.next(false);
          this._action$.next({
            id: actionId,
            type: userActionType || ActionType.UPDATE,
            status: 'error',
            httpError: err.error,
          });
        },
      });
  }

  public updateMinorItem(item: T, idField: string = 'id', nameField: string = 'naam') {
    const actionId = uuidv4();
    this._loadingMinorAction$.next(true);
    this._action$.next({
      id: actionId,
      type: ActionType.UPDATE_MINOR,
      status: 'pending',
    });

    this.updateResource(this.selectedMutationCallback(item))
      .pipe(
        take(1),
        RxJSUtils.operatorFunctionsToOperatorFunction(this.selectedOperators),
        map((res) => res as any),
      )
      .subscribe({
        next: (res) => {
          this._action$.next({
            id: actionId,
            type: ActionType.UPDATE_MINOR,
            status: 'success',
            data: {
              id: res[idField],
              name: res[nameField],
            },
          });

          const updatedItems = this.itemsValue.map((item: any) => {
            if (idField in item && item[idField] === res[idField]) {
              return res;
            } else {
              return item;
            }
          });
          this._selected$.next(res);
          this._items$.next(updatedItems);
        },
        error: (err: RgsHttpErrorResponse) => {
          this._action$.next({
            id: actionId,
            type: ActionType.UPDATE_MINOR,
            status: 'error',
            httpError: err.error,
          });
        },
      });
  }

  public deleteItemById(id: string | number, idField: string = 'id', nameField: string = 'naam') {
    const actionId = uuidv4();
    this._loadingAction$.next(true);
    this._action$.next({
      id: actionId,
      type: ActionType.DELETE,
      status: 'pending',
    });

    this.deleteResourceById(id)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this._loadingAction$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.DELETE,
            status: 'success',
            data: {
              id: 'body' in res ? res.body[idField] : res[idField],
              name: 'body' in res ? res.body[nameField] : res[idField],
            },
          });

          const updatedItems = this.itemsValue.filter((item: any) => {
            return item.id !== id;
          });

          this._items$.next(updatedItems);
          this._count$.next(this.countValue - 1);
        },
        error: (err: RgsHttpErrorResponse) => {
          this._loadingAction$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.DELETE,
            status: 'error',
            httpError: err.error,
          });
        },
      });
  }

  public duplicateItem(item: T, idField: string = 'id', nameField: string = 'naam', callback?: (item: T) => T) {
    const actionId = uuidv4();
    this._loadingAction$.next(true);
    this._action$.next({
      id: actionId,
      type: ActionType.DUPLICATE,
      status: 'pending',
    });
    let duplicated: T = {
      ...item,
      [idField]: undefined,
      naam: SuffixUtils.formatWithSuffix(
        String(item[nameField as keyof T] ?? ''),
        this.commonStateTranslateService.instant('suffixes.copy'),
      ),
    };

    if (callback) {
      duplicated = callback(duplicated);
    }

    this.createResource({ body: this.selectedMutationCallback(duplicated) })
      .pipe(
        take(1),
        RxJSUtils.operatorFunctionsToOperatorFunction(this.selectedOperators),
        map((res) => res as any),
      )
      .subscribe({
        next: (res) => {
          this._loadingAction$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.DUPLICATE,
            status: 'success',
            data: {
              id: res[idField],
              name: res[nameField],
            },
          });
          this._items$.next([...this.itemsValue, res]);
          this._count$.next(this.countValue + 1);
        },
        error: (err: RgsHttpErrorResponse) => {
          this._loadingAction$.next(false);
          this._action$.next({
            id: actionId,
            type: ActionType.DUPLICATE,
            status: 'error',
            httpError: err.error,
          });
        },
      });
  }

  public resetAction() {
    this._action$.next(undefined);
  }

  public updateItemValue(item: T) {
    this._selected$.next(item);
  }

  public get selectedValue(): T | undefined {
    return this._selected$.getValue();
  }

  public get loadingSelectedValue(): boolean {
    return this._loadingSelected$.getValue();
  }

  public get itemsValue(): T[] {
    return this._items$.getValue();
  }

  public get loadingItemsValue(): boolean {
    return this._loadingItems$.getValue();
  }

  public get loadingActionValue(): boolean {
    return this._loadingAction$.getValue();
  }

  public get actionValue(): Action | undefined {
    return this._action$.getValue();
  }

  public get countValue(): number {
    return this._count$.getValue();
  }
  public set itemsValue(value: T[]) {
    this._items$.next(value);
  }
}
